import { Fragment, type FC, type ReactNode } from 'react';

import { CheckCircle, FileArchive } from 'lucide-react';

import type { ArtifactContent } from '@features/integrations/api/create-agent.types';
import { cn } from '@shared/utils/tailwind.utils';

import { STEP, STEP_CONTENT_MAP, STEP_LABELS } from '../../create-agent.utils';
import type { Step } from '../../create-agent.utils';

import type { AgentDetailsFormValues } from './generic-agent-details.schema';

interface StepSectionHeaderProps {
  title: string;
  details: string;
  className?: string;
}

const StepSectionHeader: FC<StepSectionHeaderProps> = ({ title, details, className = 'mt-1.5' }) => (
  <div className={cn('flex flex-col gap-0.5 pl-0.5', className)}>
    <span className="font-semibold text-md tracking-tight text-zinc-100 block w-full">{title}</span>
    <div className="text-sm text-muted-foreground tracking-tight leading-relaxed">{details}</div>
  </div>
);

interface SuccessHeaderProps {
  message: string;
  file?: File;
  alreadyUploadedArtifacts?: ArtifactContent[];
  name?: string;
  architecture?: string;
  agentHost?: string;
  description?: string;
}

const SuccessHeader: FC<SuccessHeaderProps> = ({
  message,
  file,
  alreadyUploadedArtifacts,
  name,
  architecture,
  agentHost,
  description,
}) => {
  const agentDetailRows = [
    { label: 'Agent Name', value: name },
    { label: 'Architecture', value: architecture },
    { label: 'Agent Host', value: agentHost },
    { label: 'Description', value: description },
  ].filter((row) => row.value);

  const renderFileCard = (filename: string, subtitle?: ReactNode, key?: string) => (
    <div
      key={key}
      className="group flex items-center gap-3 p-2.5 bg-zinc-900/30 border border-zinc-800/40 rounded-lg ease-out ml-8"
    >
      <div className="h-8 w-8 rounded-md bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
        <FileArchive className="h-4 w-4" />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-xs font-medium text-zinc-300 truncate font-mono tracking-tight">{filename}</span>
        {subtitle}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3.5 p-4 bg-emerald-950/10 border border-emerald-500/20 rounded-xl select-none">
      <div className="flex items-center gap-3">
        <CheckCircle className="h-5 w-5 text-emerald-500/90 shrink-0" />
        <span className="text-md font-semibold text-zinc-100 tracking-tight">{message}</span>
      </div>

      {file &&
        renderFileCard(
          file.name,
          <span className="text-[10px] text-zinc-500 font-mono tracking-tight">
            {(file.size / 1024).toFixed(1)} KB
          </span>,
        )}

      {!file && alreadyUploadedArtifacts?.map((a) => renderFileCard(a.artifact, undefined, a.artifact))}

      {agentDetailRows.length > 0 && (
        <div className="grid grid-cols-[100px_1fr] gap-x-4 gap-y-2 ml-8 text-xs font-mono select-text min-w-0">
          {agentDetailRows.map((row) => (
            <Fragment key={`agent-detail-${row.label}`}>
              <span className="text-zinc-400 text-right font-sans font-medium select-none pr-1">{row.label}</span>
              <div className="min-w-0 flex items-center">
                <span className="text-zinc-200 font-normal truncate block w-full" title={row.value}>
                  {row.value}
                </span>
              </div>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export const ArtifactsFoundSuccess = ({ artifacts }: { artifacts?: ArtifactContent[] }) => {
  if (!artifacts || artifacts.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3.5 px-4 py-[18.5px] bg-emerald-950/10 border border-emerald-500/20 rounded-xl select-none">
        <div className="flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-500/90 shrink-0" />
          <span className="text-md font-semibold text-zinc-100 tracking-tight">
            Required artifacts already uploaded!
          </span>
        </div>

        {artifacts.map((a) => (
          <div
            key={a.artifact}
            className="group flex items-center gap-3 p-2.5 bg-zinc-900/30 border border-zinc-800/40 rounded-lg ml-8"
          >
            <div className="h-8 w-8 rounded-md bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
              <FileArchive className="h-4 w-4" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-medium text-zinc-300 truncate font-mono tracking-tight">{a.artifact}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-0.5 pl-0.5">
        <div className="text-sm text-muted-foreground tracking-tight leading-relaxed">
          Click 'Next' to proceed or 'Upload New' to upload a new artifact.
        </div>
      </div>
    </div>
  );
};

interface BaseHeaderLayoutProps {
  stepLabel: string;
  headerTitle: string;
  showBackButton: boolean;
  renderBackButton: () => ReactNode;
  children: ReactNode;
}

const BaseHeaderLayout = ({
  stepLabel,
  headerTitle,
  showBackButton,
  renderBackButton,
  children,
}: BaseHeaderLayoutProps) => ({
  title: (
    <div className="flex flex-col gap-2 items-start">
      <span className="text-[10px] font-bold text-primary/80 uppercase tracking-widest block">{stepLabel}</span>
      <div className="flex items-center gap-2">
        {showBackButton && renderBackButton()}
        <span className="font-bold text-foreground tracking-tight">{headerTitle}</span>
      </div>
    </div>
  ),
  description: children,
});

interface HeaderConfig {
  title: ReactNode;
  description: ReactNode;
}

interface CreateAgentHeaderConfigArgs {
  currentStep: Step;
  stagedFile: File[];
  alreadyUploadedArtifacts: ArtifactContent[];
  agentFormDetails: AgentDetailsFormValues | null;
  renderBackButton: () => ReactNode;
}

export const getCreateAgentHeaderConfig = (args: CreateAgentHeaderConfigArgs): HeaderConfig => {
  const { currentStep, stagedFile, alreadyUploadedArtifacts, agentFormDetails, renderBackButton } = args;

  if (currentStep === STEP.AGENT_TYPE_SELECTION) {
    return {
      title: 'SAP Deployment',
      description: (
        <span className="text-sm text-muted-foreground block">Choose how you would like to connect to SAP</span>
      ),
    };
  }

  const content = STEP_CONTENT_MAP[currentStep];
  const stepLabel = STEP_LABELS[currentStep] || '';
  const targetFile = Array.isArray(stagedFile) ? stagedFile[0] : undefined;

  if (!content) {
    console.error(
      `[getCreateAgentHeaderConfig] Missing content mapping data schema for active step key: "${currentStep}"`,
    );
    return {
      title: 'Configuration Error',
      description: (
        <span className="text-sm text-destructive block">Step content could not be resolved. Please reload.</span>
      ),
    };
  }

  const stepsWithBackButton: (typeof STEP)[keyof typeof STEP][] = [
    STEP.STANDALONE_AGENT_UPLOAD_ARTIFACT,
    STEP.OP_AGENT_ENTER_DETAILS,
  ];
  const showBackButton = stepsWithBackButton.includes(currentStep);

  const stepChildrenMap: Record<string, ReactNode> = {
    // Standalone Agent Steps
    [STEP.STANDALONE_AGENT_UPLOAD_ARTIFACT]: (
      <StepSectionHeader title={content.stepTitle} details={content.stepDetails} className="mb-2" />
    ),
    [STEP.STANDALONE_AGENT_ENTER_DETAILS]: (
      <div className="w-full flex flex-col gap-4">
        {targetFile && <SuccessHeader message="Upload successful!" file={targetFile} />}
        <StepSectionHeader title={content.stepTitle} details={content.stepDetails} />
      </div>
    ),
    [STEP.STANDALONE_AGENT_GENERATE_BUNDLE]: (
      <div className="w-full flex flex-col gap-4">
        <SuccessHeader
          {...agentFormDetails}
          message="Agent generated successfully!"
          file={targetFile}
          alreadyUploadedArtifacts={alreadyUploadedArtifacts}
        />
        <StepSectionHeader title={content.stepTitle} details={content.stepDetails} className="mt-1" />
      </div>
    ),

    // OP Integration Steps
    [STEP.OP_AGENT_ENTER_DETAILS]: <StepSectionHeader title={content.stepTitle} details={content.stepDetails} />,
    [STEP.OP_AGENT_GENERATE_BUNDLE]: (
      <div className="w-full flex flex-col gap-4">
        <SuccessHeader {...agentFormDetails} message="OP deployment generated successfully!" />
        <StepSectionHeader title={content.stepTitle} details={content.stepDetails} className="mt-1" />
      </div>
    ),
  };

  const currentChildren = stepChildrenMap[currentStep];

  if (!currentChildren) {
    console.error(
      `[getCreateAgentHeaderConfig] Element children mapping unresolvable or missing for step key: "${currentStep}"`,
    );
    return {
      title: 'Configuration Error',
      description: <span className="text-sm text-destructive block">Step layout children missing. Please reload.</span>,
    };
  }

  return BaseHeaderLayout({
    stepLabel,
    headerTitle: content.headerTitle,
    showBackButton,
    renderBackButton,
    children: currentChildren,
  });
};
